import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;

/** A flake of ash drifting down from the eruption's cloud; a few still glow. */
interface Flake {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  glow: boolean;
  rank: number;
  shown: number;
  sway: number;
}

/**
 * Etna's hero board (2026-10-16), after the theme's own photograph (cover.webp) and Dock Town's Etna:
 * Mount Etna at night. A sky of Etna's black high up warming to its red low down; the volcano a dark
 * mass in the middle, one broad summit and long gentle flanks, layers of heat glowing under its top
 * surface; in the crater a pool of lava, its far wall the mountain's rock, its near lip brimming with a
 * bright line, the lava seething in it, slow and heavy, cooling toward crimson at rest; veins of lava
 * running down from the lip along the cone's fall line, branching smoothly; smoke rising from the
 * crater and leaning away on the wind; a black ridge across the foot; ash falling. The counter (the
 * charge) brings more ash down and makes the lava seethe higher, more and quicker (owner: no explosion,
 * no fireworks). The page's wallpaper is the ash alone. Laid out by the stage's height, fixed seeds.
 */
@Component( {
  selector: 'app-hero-etna',
  template: '<canvas #canvas class="het-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.het-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroEtnaComponent extends HeroMotifBase {

  private back: HTMLCanvasElement | null = null;
  private front: HTMLCanvasElement | null = null;
  private flakes: Flake[] = [];
  private crater: [ number, number ] = [ 0, 0 ];
  /** The crater's lava surface along its near lip (page pixels), set when the scene is baked. */
  private lip: [ number, number ][] = [];
  /** The crater's lava pool: at each x, its far wall's top and its near lip. */
  private pool: [ number, number, number ][] = [];
  /** The lava pool's ellipse: centre x, y, radii. */
  private poolShape: [ number, number, number, number ] = [ 0, 0, 0, 0 ];
  /** The lava seething in the crater: blobs tossed up a little and falling back, bubbles swelling and bursting. */
  private blobs: { x: number; y: number; base: number; vy: number; vx: number; age: number; life: number; size: number; drop: boolean }[] = [];
  private seethe = 0;
  private scaleK = 1;
  private UW = 0;

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.2;
  }

  protected colorsChanged(): void {
    this.back = null;
    this.front = null;
  }

  protected build(): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK = Math.max( 0.55, Math.min( 1.25, H / 760 ) );
    const UW = this.UW = Math.min( W, H * 2.1 );
    this.back = null;
    this.front = null;
    // the summit, its crater and its lava in the middle of the stage (owner)
    this.crater = [ W / 2, H * 0.24 - 50 ];
    const fr = this.seededLocal( 13 );
    this.flakes = [];
    for ( let i = 0; i < 260; i++ ) {
      this.flakes.push( {
        x: fr() * W, y: fr() * H, vx: ( 6 + fr() * 14 ) * k, vy: ( 12 + fr() * 22 ) * k, size: ( 1 + fr() * 2.2 ) * k,
        glow: fr() < 0.14, rank: i < 26 ? i / 260 : fr(), shown: 0, sway: fr() * 6.28,
      } );
    }
  }

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    if ( this.frozen ) {
      // the page's wallpaper: the ash alone, still, all down the page
      this.drawWallAsh( ctx );
      return;
    }
    if ( !this.back ) { this.bake( ctx.getTransform().a ); }
    if ( !this.back || !this.front ) { return; }
    ctx.drawImage( this.back, 0, 0, W, H );
    this.drawSmoke( ctx );
    this.drawCrater( ctx, dt );
    ctx.drawImage( this.front, 0, 0, W, H );
    this.drawAsh( ctx, dt );
  }

  /**
   * The fumes: puffs of smoke rising from the crater, swaying as they climb, leaning away on the wind,
   * swelling and thinning out as they go; dark, lit red from below near the vent (owner: smoke rising).
   */
  private drawSmoke( ctx: CanvasRenderingContext2D ): void {
    const k = this.scaleK;
    const H = this.height;
    const UW = this.UW;
    const [ char, red, orange ] = this.five();
    const [ cx, cy ] = this.crater;
    const now = performance.now() / 1000;
    // the plume as it was painted (owner liked it), its puffs now climbing along it: many big soft
    // overlapping puffs, so it reads as one cloud, each fading in at the vent and out at the top
    const n = 80, life = 26;
    for ( let i = 0; i < n; i++ ) {
      const s1 = ( i * 7919 % 1000 ) / 1000, s2 = ( i * 104729 % 1000 ) / 1000, s3 = ( i * 1299709 % 1000 ) / 1000;
      const age = ( ( now + i * life / n ) % life + life ) % life;
      const f = age / life;
      const x = cx + UW * ( 0.02 + f * 0.3 ) + ( s1 - 0.5 ) * UW * 0.05 * ( 0.3 + f ) + Math.sin( age * 0.3 + s3 * 6.28 ) * UW * 0.01 * f;
      const y = cy - H * ( 0.04 + f * 0.24 ) + ( s2 - 0.5 ) * H * 0.04;
      const r = ( 26 + f * 70 + s3 * 30 ) * k;
      const lit = Math.max( 0, 1 - f * 1.6 );
      const tone = this.mix( this.mix( char, [ 70, 62, 62 ], 0.4 ), red, 0.35 * lit );
      const a = 0.4 * ( 1 - f * 0.5 ) * Math.min( 1, f / 0.06 ) * Math.min( 1, ( 1 - f ) / 0.2 );
      const pg = ctx.createRadialGradient( x, y + r * 0.3, 0, x, y, r );
      pg.addColorStop( 0, this.rgba( this.mix( tone, orange, 0.25 * lit ), a ) );
      pg.addColorStop( 1, this.rgba( tone, 0 ) );
      ctx.fillStyle = pg;
      ctx.fillRect( x - r, y - r, r * 2, r * 2 );
    }
  }

  /**
   * The crater: a wide low glow over its mouth, and the lava in it seething (owner): bubbles swelling
   * on its surface and bursting, blobs tossed up a little and falling back in, a few at rest, more and a
   * little higher when the counter counts. Never a fountain or fireworks: no trails, no sparks, low.
   */
  private drawCrater( ctx: CanvasRenderingContext2D, dt: number ): void {
    const k = this.scaleK;
    const [ , red, orange, gold ] = this.five();
    const [ cx, cy ] = this.crater;
    const br = 0.4 + 0.08 * Math.sin( this.time * W0 * 3 );
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    // a wide low glow over the mouth, lighting the fumes above it, not a ball of light
    ctx.translate( cx, cy - 6 * k );
    ctx.scale( this.UW * 0.07, 22 * k );
    const cg = ctx.createRadialGradient( 0, 0.2, 0, 0, 0.2, 1 );
    cg.addColorStop( 0, this.rgba( this.mix( orange, red, 0.4 ), br * 0.5 ) );
    cg.addColorStop( 0.6, this.rgba( red, br * 0.15 ) );
    cg.addColorStop( 1, this.rgba( red, 0 ) );
    ctx.fillStyle = cg;
    ctx.fillRect( -1, -1, 2, 2 );
    ctx.restore();
    if ( !this.lip.length ) { return; }
    const e = this.energy;
    const hotCore = this.mix( gold, [ 255, 240, 200 ], 0.25 );
    // slow and heavy (owner): lava is thick, it lifts and sags back
    // quicker when the counter counts (owner)
    const gravity = 90 * k * ( 1 + 1.5 * e );
    if ( dt > 0 ) {
      this.seethe += dt * ( 1.5 + 12 * e );
      while ( this.seethe >= 1 ) {
        this.seethe -= 1;
        if ( this.blobs.length > 24 + 30 * e ) { break; }
        // from inside the pool, never in front of its lip (owner)
        // toward the middle of the pool, away from its ends (owner)
        const [ x, top, lipY ] = this.pool[ Math.floor( this.pool.length * ( 0.3 + 0.4 * Math.random() ) ) ];
        const y = top + ( lipY - top ) * ( 0.4 + 0.45 * Math.random() );
        const drop = Math.random() < 0.4;
        const high = ( 3 + Math.random() * 7 ) * k * ( 1 + 2.2 * e );
        this.blobs.push( {
          x, y, base: y, vx: ( Math.random() - 0.5 ) * 5 * k, vy: drop ? -Math.sqrt( 2 * gravity * high ) : 0,
          age: 0, life: drop ? 2 * Math.sqrt( 2 * high / gravity ) : 1.4 + Math.random() * 1.6, size: ( drop ? 2.2 + Math.random() * 2 : 3.5 + Math.random() * 4 ) * k, drop,
        } );
      }
      for ( const b of this.blobs ) {
        b.age += dt;
        if ( b.drop ) { b.vy += gravity * dt; b.x += b.vx * dt; b.y += b.vy * dt; }
      }
      this.blobs = this.blobs.filter( b => b.age < b.life && ( !b.drop || b.y <= b.base + 1 ) );
    }
    // drawn as molten rock, not light: solid, glowing hot inside
    const lavaRed = this.mix( red, [ 232, 60, 30 ], 0.5 );
    ctx.save();
    // only above the near lip: what seethes stays in the crater, the mountain's front hides the rest
    ctx.beginPath();
    ctx.moveTo( this.lip[ 0 ][ 0 ], 0 );
    for ( const [ lx, ly ] of this.lip ) { ctx.lineTo( lx, ly ); }
    ctx.lineTo( this.lip[ this.lip.length - 1 ][ 0 ], 0 );
    ctx.closePath();
    ctx.clip();
    // at rest the pool cools a little toward crimson; when the counter counts it runs hot again (owner)
    const [ px, py, prx, pry ] = this.poolShape;
    const cool = 1 - e;
    if ( cool > 0.02 && prx > 0 ) {
      ctx.fillStyle = this.rgba( this.mix( red, [ 150, 18, 28 ], 0.45 ), 0.4 * cool );
      ctx.beginPath();
      ctx.ellipse( px, py, prx, pry, 0, 0, Math.PI * 2 );
      ctx.fill();
    }
    for ( const b of this.blobs ) {
      const f = b.age / b.life;
      if ( b.drop ) {
        // a heavy gob, sagging a little as it hangs at the top
        const sag = 1 + 0.25 * ( 1 - Math.min( 1, Math.abs( b.vy ) / ( 60 * k ) ) );
        ctx.save();
        ctx.translate( b.x, b.y );
        ctx.scale( sag, 1 / sag );
        const g = ctx.createRadialGradient( -b.size * 0.2, -b.size * 0.2, 0, 0, 0, b.size );
        // no dark rim (owner): hot inside, the lava's own red at its edge, softly
        g.addColorStop( 0, this.rgba( hotCore, 1 ) );
        g.addColorStop( 0.5, this.rgba( orange, 1 ) );
        g.addColorStop( 0.85, this.rgba( lavaRed, 0.95 ) );
        g.addColorStop( 1, this.rgba( lavaRed, 0 ) );
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc( 0, 0, b.size, 0, Math.PI * 2 );
        ctx.fill();
        ctx.restore();
      } else {
        // a bubble of lava swelling slowly, then sinking back
        const grow = Math.sin( Math.min( 1, f / 0.8 ) * Math.PI / 2 );
        const sink = f > 0.8 ? ( f - 0.8 ) / 0.2 : 0;
        const r = b.size * grow * ( 1 - sink * 0.9 );
        if ( r < 0.4 ) { continue; }
        const g = ctx.createRadialGradient( b.x, b.base - r * 0.2, 0, b.x, b.base, r * 1.3 );
        g.addColorStop( 0, this.rgba( this.mix( orange, hotCore, 0.5 ), 1 ) );
        g.addColorStop( 0.6, this.rgba( this.mix( lavaRed, orange, 0.4 ), 1 ) );
        g.addColorStop( 1, this.rgba( lavaRed, 0.85 ) );
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.ellipse( b.x, b.base + 0.5, r * 1.3, r, 0, Math.PI, Math.PI * 2 );
        ctx.closePath();
        ctx.fill();
      }
    }
    ctx.restore();
    // the bright line of lava along the lip, in front of all of it (owner)
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const lx0 = this.lip[ 0 ][ 0 ], lx1 = this.lip[ this.lip.length - 1 ][ 0 ];
    const lg = ctx.createLinearGradient( lx0, 0, lx1, 0 );
    lg.addColorStop( 0, this.rgba( orange, 0.8 ) );
    // the orange runs a good way in from each end (owner)
    lg.addColorStop( 0.22, this.rgba( orange, 0.8 ) );
    lg.addColorStop( 0.78, this.rgba( orange, 0.8 ) );
    lg.addColorStop( 0.5, this.rgba( hotCore, 0.85 ) );
    lg.addColorStop( 1, this.rgba( orange, 0.8 ) );
    ctx.strokeStyle = lg;
    ctx.lineWidth = Math.max( 0.8, 1 * k );
    ctx.beginPath();
    const cut = Math.floor( this.lip.length * 0.04 );
    for ( let i = cut; i < this.lip.length - cut; i++ ) { ctx.lineTo( this.lip[ i ][ 0 ], this.lip[ i ][ 1 ] ); }
    ctx.stroke();
    ctx.restore();
  }

  /**
   * The ash: grey flakes drifting down from the eruption's cloud, a few still glowing; a few at rest,
   * far more when the counter counts, called out somewhere and fading in, fading away when sent off.
   */
  private drawAsh( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    const e = this.energy;
    const [ , red, orange, , ash ] = this.five();
    const share = 0.1 + 0.9 * e;
    for ( const fl of this.flakes ) {
      const want = fl.rank <= share ? 1 : 0;
      if ( dt > 0 ) {
        if ( want && fl.shown < 0.02 ) { fl.x = Math.random() * W; fl.y = Math.random() * H * 0.6; }
        fl.shown += ( want - fl.shown ) * Math.min( 1, dt * ( want > fl.shown ? 1 : 0.5 ) );
        fl.sway += dt * 1.3;
        fl.x += ( fl.vx * ( 1 + e ) + Math.sin( fl.sway ) * 6 * this.scaleK ) * dt;
        fl.y += fl.vy * ( 1 + 0.6 * e ) * dt;
        if ( fl.y > H + 6 ) { fl.y = -6; fl.x = Math.random() * W; }
        if ( fl.x > W + 6 ) { fl.x = -6; }
      } else {
        fl.shown = want;
      }
      if ( fl.shown < 0.02 ) { continue; }
      if ( fl.glow ) {
        ctx.fillStyle = this.rgba( this.mix( orange, red, 0.3 ), 0.85 * fl.shown );
      } else {
        ctx.fillStyle = this.rgba( this.mix( ash, [ 60, 56, 56 ], 0.45 ), 0.6 * fl.shown );
      }
      ctx.beginPath();
      ctx.ellipse( fl.x, fl.y, fl.size, fl.size * 0.7, fl.sway, 0, Math.PI * 2 );
      ctx.fill();
    }
  }

  /** The wallpaper: the ash scattered still down the page, a few flakes glowing. */
  private drawWallAsh( ctx: CanvasRenderingContext2D ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const [ , red, orange, , ash ] = this.five();
    const r = this.seededLocal( 5 );
    const n = Math.max( 10, Math.round( W * H / 9000 ) );
    for ( let i = 0; i < n; i++ ) {
      const glow = r() < 0.12;
      ctx.fillStyle = glow ? this.rgba( this.mix( orange, red, 0.3 ), 0.7 ) : this.rgba( this.mix( ash, [ 60, 56, 56 ], 0.45 ), 0.5 );
      ctx.beginPath();
      ctx.ellipse( r() * W, r() * H, ( 1 + r() * 2 ) * k, ( 0.7 + r() * 1.4 ) * k, r() * 6.28, 0, Math.PI * 2 );
      ctx.fill();
    }
  }

  /**
   * The still parts, painted once: the night sky, the eruption's red glow, the volcano with its lava
   * glowing cracks on the back layer; the black ridge across the foot on the front one.
   */
  private bake( scale: number ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const UW = this.UW;
    const [ char, red, orange, gold ] = this.five();
    const canvas = () => {
      const c = document.createElement( 'canvas' );
      c.width = Math.ceil( W * scale );
      c.height = Math.ceil( H * scale );
      const g = c.getContext( '2d' )!;
      g.scale( scale, scale );
      return { c, g };
    };
    const { c, g } = canvas();
    const black: RGB = [ 8, 7, 9 ];
    // the sky: Etna's black high up, warming down to Etna's red behind the mountain (owner)
    const sky = g.createLinearGradient( 0, 0, 0, H * 0.85 );
    sky.addColorStop( 0, this.rgba( this.mix( char, black, 0.5 ), 1 ) );
    sky.addColorStop( 0.5, this.rgba( this.mix( this.mix( char, red, 0.2 ), black, 0.25 ), 1 ) );
    sky.addColorStop( 1, this.rgba( this.mix( red, char, 0.4 ), 1 ) );
    g.fillStyle = sky;
    g.fillRect( 0, 0, W, H );
    // the eruption lighting the sky red at the left
    const [ cx, cy ] = this.crater;
    const R = Math.max( W, H );
    const glow = g.createRadialGradient( cx - UW * 0.12, cy, 0, cx - UW * 0.12, cy, R * 0.6 );
    glow.addColorStop( 0, this.rgba( this.mix( red, orange, 0.3 ), 0.7 ) );
    glow.addColorStop( 0.4, this.rgba( this.mix( red, char, 0.3 ), 0.38 ) );
    glow.addColorStop( 1, this.rgba( red, 0 ) );
    g.fillStyle = glow;
    g.fillRect( 0, 0, W, H );

    // the volcano: a broad dark cone, its summit in the middle, the slopes concave (steep at the top, easing out), running on to the edges
    const foot = H * 0.87;
    const ridge = ( x: number ) => {
      // Etna as photographs show it (owner): one broad summit, its top a little flat round the
      // craters, and long gentle concave flanks spreading far out to either side, the right one a
      // little longer, both reaching the ground
      const u = ( x - cx ) / UW;
      // a broad summit (owner)
      const top = 0.06;
      const a = Math.abs( u );
      if ( a < top ) { return cy + H * 0.006 * ( a / top ) * ( a / top ); }
      // the right a little longer than the left, reaching the ground (owner)
      // on a wide stage the right flank stretches to reach the ground just past the edge, so it fills
      // the stage to its end (owner)
      const reach = u < 0 ? 0.8 : Math.max( 0.88, ( W - cx ) / UW + 0.02 );
      const f = Math.min( 1, ( a - top ) / reach );
      // the right flank eases out just above the ridge in front, so it meets the stage's edge smoothly,
      // with no kink where it would dip behind the ridge
      const drop = u < 0 ? foot - cy : foot - H * 0.07 - cy;
      return cy + H * 0.006 + ( drop - H * 0.006 ) * ( 1 - Math.pow( 1 - f, 2.3 ) );
    };
    // the crater: a shallow wide mouth in the summit, its far rim (the skyline) dipping a little between
    // two uneven lips, the left one higher; the near lip lower still, in front of the crater
    // the lava at the summit runs the whole width of its broad top (owner)
    const mouth = UW * 0.066;
    const rimAt = ( x: number ) => {
      const d = ( x - cx ) / mouth;
      if ( Math.abs( d ) >= 1 ) { return ridge( x ); }
      // its middle a little higher (owner)
      const dip = Math.pow( 1 - d * d, 1.5 ) * H * 0.0005 * ( 1 - 0.25 * d );
      // a little unevenness toward its ends, none in the middle (owner: no dent there)
      return ridge( x ) + dip + Math.sin( d * 9 ) * d * d * H * 0.0012;
    };
    const lipAt = ( x: number ) => {
      // the crater's inside closes at the ends of the bright line on the lip, both sides (owner)
      const d = ( x - cx ) / ( mouth * 0.92 );
      if ( Math.abs( d ) >= 1 ) { return rimAt( x ); }
      // lower still, so the lava pool shows between the far wall and the lip (owner)
      return rimAt( x ) + Math.pow( 1 - d * d, 0.6 ) * H * 0.024 + Math.sin( d * 13 ) * H * 0.001 * ( 1 - d * d );
    };
    const outline = () => {
      g.beginPath();
      g.moveTo( 0, H );
      for ( let x = 0; x <= W; x += 2 ) {
        g.lineTo( x, rimAt( x ) );
      }
      g.lineTo( W, H );
      g.closePath();
    };
    outline();
    const mg = g.createLinearGradient( cx - UW * 0.5, 0, cx + UW * 0.5, 0 );
    // lit a dull red at the left by the eruption's glow, the right lost in the dark
    // darker than the sky behind it, so it stands out as a mass (owner)
    mg.addColorStop( 0, this.rgba( this.mix( this.mix( char, red, 0.12 ), black, 0.6 ), 1 ) );
    mg.addColorStop( 0.45, this.rgba( this.mix( this.mix( char, red, 0.1 ), black, 0.55 ), 1 ) );
    mg.addColorStop( 0.6, this.rgba( this.mix( char, black, 0.65 ), 1 ) );
    mg.addColorStop( 1, this.rgba( this.mix( char, black, 0.7 ), 1 ) );
    g.fillStyle = mg;
    g.fill();
    // the red light from the summit down its upper slopes, soft
    g.save();
    outline();
    g.clip();
    const sg = g.createRadialGradient( cx, cy, 0, cx, cy, UW * 0.35 );
    sg.addColorStop( 0, this.rgba( this.mix( red, orange, 0.4 ), 0.25 ) );
    sg.addColorStop( 0.5, this.rgba( red, 0.05 ) );
    sg.addColorStop( 1, this.rgba( red, 0 ) );
    g.fillStyle = sg;
    g.fillRect( 0, 0, W, H );
    // a faint rim of light along the right ridge's skyline, the far snow catching the night
    g.restore();
    g.strokeStyle = this.rgba( this.mix( [ 120, 130, 160 ], char, 0.4 ), 0.22 );
    g.lineWidth = Math.max( 1, 1.4 * k );
    g.beginPath();
    for ( let x = cx + UW * 0.05; x <= Math.min( W, cx + UW * 0.9 ); x += 2 ) { g.lineTo( x, ridge( x ) + 1 ); }
    g.stroke();

    // the lava as Dock Town's Etna has it (owner): the summit's rock hot, glowing red from the heat
    // inside, gone a third of the way down; cracks lit from within running down the cone from the rim,
    // wandering and branching, fading as they go; the crater bright, its lava brimming over the lip
    const ember: RGB = this.mix( [ 226, 88, 31 ], orange, 0.2 );
    const deep: RGB = [ 168, 42, 20 ];
    const hotThread = this.mix( gold, ember, 0.35 );
    g.save();
    outline();
    g.clip();
    // the heat along the whole top surface (owner): the rock glowing just under the skyline in many
    // soft layers, dark red, deepening down the flanks and fading toward the ends of the summit ridge
    const skyline = () => {
      g.beginPath();
      for ( let x = cx - UW * 0.42; x <= cx + UW * 0.48; x += 3 ) { g.lineTo( x, rimAt( x ) ); }
    };
    const along = ( alpha: number, c: RGB ) => {
      const lg = g.createLinearGradient( cx - UW * 0.42, 0, cx + UW * 0.48, 0 );
      lg.addColorStop( 0, this.rgba( c, 0 ) );
      lg.addColorStop( 0.35, this.rgba( c, alpha ) );
      lg.addColorStop( 0.55, this.rgba( c, alpha ) );
      lg.addColorStop( 1, this.rgba( c, 0 ) );
      return lg;
    };
    g.lineJoin = 'round';
    for ( const [ wd, al, c ] of [ [ H * 0.3, 0.08, this.mix( deep, black, 0.6 ) ], [ H * 0.24, 0.1, this.mix( deep, black, 0.5 ) ], [ H * 0.18, 0.12, this.mix( deep, black, 0.4 ) ], [ H * 0.13, 0.14, this.mix( deep, black, 0.3 ) ], [ H * 0.085, 0.16, this.mix( deep, black, 0.2 ) ], [ H * 0.05, 0.18, deep ], [ H * 0.025, 0.2, this.mix( deep, ember, 0.25 ) ] ] as [ number, number, RGB ][] ) {
      skyline();
      g.strokeStyle = along( al, c );
      g.lineWidth = wd;
      g.stroke();
    }
    const cr = this.seededLocal( 23 );
    const cracks: [ number, number ][][] = [];
    const branches: [ number, number ][][] = [];
    // the veins leave the lava's edge all along the lip, the middle ones too (owner), and run down the
    // fall line of the cone: radiating from a point over the summit, so those toward the sides lean
    // out with the flanks; each meanders a little and some branch
    const apexY = cy - mouth * 0.9;
    const fall = ( sx: number, sy: number, s2: number, wob: number ): [ number, number ] => {
      const x = sx + ( sx - cx ) * s2 * 9 + wob;
      const y = sy + ( sy - apexY ) * s2 * 9;
      return [ x, Math.max( y, ridge( x ) + 3 * k ) ];
    };
    const nCracks = 17;
    for ( let n = 0; n < nCracks; n++ ) {
      const sx0 = cx - mouth * 0.92 + mouth * 1.84 * ( n + 0.25 + 0.5 * cr() ) / nCracks;
      const sy0 = lipAt( sx0 ) - 1;
      const reach = 0.05 + 0.07 * cr(), steps = 22;
      const line: [ number, number ][] = [ [ sx0, sy0 ] ], fork: [ number, number ][] = [];
      let wob = 0, dw = 0, fw = 0;
      // a branch leaves its vein tangentially and curves away from it smoothly (owner), easing out
      const fq = Math.floor( steps * ( 0.35 + 0.2 * cr() ) );
      const fside = cr() < 0.5 ? -1 : 1;
      const fspread = ( 10 + 12 * cr() ) * k;
      for ( let q = 1; q <= steps; q++ ) {
        const t = q / steps;
        dw += ( cr() - 0.5 ) * 0.45 * k;
        dw *= 0.9;
        wob += dw;
        const p2 = fall( sx0, sy0, reach * t, wob );
        line.push( p2 );
        if ( q === fq ) { fw = wob; }
        if ( q > fq && n % 3 === 1 ) {
          const u = ( q - fq ) / ( steps - fq );
          fork.push( fall( sx0, sy0, reach * t * ( 1 - 0.12 * u ), fw + ( wob - fw ) * 0.5 + fside * fspread * u * u * ( 3 - 2 * u ) ) );
        }
      }
      cracks.push( line );
      if ( fork.length > 2 ) { branches.push( [ line[ fq ], ...fork ] ); }
    }
    // each vein a smooth tapering ribbon (owner: better quality): its glow on the rock, its ember
    // body, the hot thread along its middle, all fading down its length
    const smooth = ( pts: [ number, number ][] ) => {
      let p2 = pts;
      for ( let it = 0; it < 2; it++ ) {
        const o: [ number, number ][] = [ p2[ 0 ] ];
        for ( let q = 0; q < p2.length - 1; q++ ) {
          const [ ax, ay ] = p2[ q ], [ bx, by ] = p2[ q + 1 ];
          o.push( [ ax * 0.75 + bx * 0.25, ay * 0.75 + by * 0.25 ], [ ax * 0.25 + bx * 0.75, ay * 0.25 + by * 0.75 ] );
        }
        o.push( p2[ p2.length - 1 ] );
        p2 = o;
      }
      return p2;
    };
    const vein = ( pts: [ number, number ][], w0: number, c: RGB, a0: number ) => {
      const L: [ number, number ][] = [], Rr: [ number, number ][] = [];
      for ( let q = 0; q < pts.length; q++ ) {
        const t = q / ( pts.length - 1 );
        const [ x, y ] = pts[ q ];
        const [ xa, ya ] = pts[ Math.max( 0, q - 1 ) ], [ xb, yb ] = pts[ Math.min( pts.length - 1, q + 1 ) ];
        const dx = xb - xa, dy = yb - ya, d = Math.hypot( dx, dy ) || 1;
        const hw = w0 * ( 1 - 0.7 * t ) / 2;
        L.push( [ x - dy / d * hw, y + dx / d * hw ] );
        Rr.push( [ x + dy / d * hw, y - dx / d * hw ] );
      }
      const [ sx, sy ] = pts[ 0 ], [ tx, ty ] = pts[ pts.length - 1 ];
      const lg = g.createLinearGradient( sx, sy, tx, ty );
      lg.addColorStop( 0, this.rgba( c, a0 ) );
      lg.addColorStop( 0.6, this.rgba( c, a0 * 0.55 ) );
      lg.addColorStop( 1, this.rgba( c, 0 ) );
      g.fillStyle = lg;
      g.beginPath();
      L.forEach( ( [ x, y ], q ) => q ? g.lineTo( x, y ) : g.moveTo( x, y ) );
      for ( let q = Rr.length - 1; q >= 0; q-- ) { g.lineTo( Rr[ q ][ 0 ], Rr[ q ][ 1 ] ); }
      g.closePath();
      g.fill();
    };
    // the branches first, fainter, blending into the rock under the main veins (owner)
    for ( const raw of branches ) {
      const pts = smooth( raw );
      vein( pts, 3 * k, ember, 0.5 );
      vein( pts, 1.1 * k, hotThread, 0.45 );
    }
    for ( const raw of cracks ) {
      const pts = smooth( raw );
      g.save();
      // its glow on the rock kept faint (owner)
      g.shadowColor = this.rgba( ember, 0.35 );
      g.shadowBlur = 5 * k * scale;
      vein( pts, 8 * k, ember, 0.07 );
      g.restore();
      vein( pts, 3.6 * k, ember, 0.95 );
      vein( pts, 1.4 * k, hotThread, 1 );
    }
    g.restore();
    // the crater's inside: between the far rim and the near lip, its far wall, dark red at the top,
    // lit ember lower down by the lava lying in it
    const x0 = cx - mouth, x1 = cx + mouth;
    g.save();
    g.beginPath();
    for ( let x = x0; x <= x1; x += 1 ) { g.lineTo( x, rimAt( x ) ); }
    for ( let x = x1; x >= x0; x -= 1 ) { g.lineTo( x, lipAt( x ) ); }
    g.closePath();
    // the far wall inside the crater is the mountain's own rock (owner), lit a little red low down
    const wall = g.createLinearGradient( 0, cy - H * 0.002, 0, cy + H * 0.03 );
    wall.addColorStop( 0, this.rgba( this.mix( this.mix( char, red, 0.1 ), black, 0.55 ), 1 ) );
    wall.addColorStop( 1, this.rgba( this.mix( this.mix( char, red, 0.35 ), black, 0.3 ), 1 ) );
    g.fillStyle = wall;
    g.fill();
    g.clip();
    // the lava pool: a round pool seen at a low angle, its near part cut off by the lip
    const poolY = lipAt( cx ) - H * 0.003;
    // spread a little: a touch flatter from above, a touch wider at the sides (owner)
    const prx = mouth * 0.95, pry = ( lipAt( cx ) - rimAt( cx ) ) * 0.62;
    this.poolShape = [ cx, poolY, prx, pry ];
    const pg2 = g.createRadialGradient( cx, poolY + pry * 0.2, 0, cx, poolY, prx );
    pg2.addColorStop( 0, this.rgba( this.mix( hotThread, [ 255, 245, 210 ], 0.2 ), 1 ) );
    pg2.addColorStop( 0.5, this.rgba( this.mix( ember, hotThread, 0.3 ), 1 ) );
    pg2.addColorStop( 1, this.rgba( ember, 1 ) );
    g.fillStyle = pg2;
    g.beginPath();
    g.ellipse( cx, poolY, prx, pry, 0, 0, Math.PI * 2 );
    g.fill();
    g.beginPath();
    g.ellipse( cx, poolY, prx, pry, 0, 0, Math.PI * 2 );
    g.clip();
    // the pool's fluid surface: soft brighter swirls drifting across it, and darker skins of cooling
    // crust floating on it
    const pr2 = this.seededLocal( 41 );
    for ( let i = 0; i < 14; i++ ) {
      const px = cx + ( pr2() - 0.5 ) * mouth * 1.8;
      const py = cy + H * ( 0.008 + pr2() * 0.018 );
      g.save();
      g.translate( px, py );
      g.scale( mouth * ( 0.12 + pr2() * 0.2 ), H * ( 0.002 + pr2() * 0.003 ) );
      const sw = g.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      const crusty = pr2() < 0.4;
      const tone = crusty ? this.mix( deep, black, 0.35 ) : this.mix( hotThread, [ 255, 245, 210 ], 0.2 );
      sw.addColorStop( 0, this.rgba( tone, crusty ? 0.55 : 0.6 ) );
      sw.addColorStop( 1, this.rgba( tone, 0 ) );
      g.fillStyle = sw;
      g.fillRect( -1, -1, 2, 2 );
      g.restore();
    }
    g.restore();
    // the lava brimming along the near lip: a soft ember glow, the bright edge, a hot thread in it,
    // and where it spills over, the tops of the cracks below
    this.lip = [];
    this.pool = [];
    for ( let x = x0; x <= x1; x += 1 ) { this.lip.push( [ x, lipAt( x ) ] ); this.pool.push( [ x, rimAt( x ), lipAt( x ) ] ); }
    const lipPath = () => {
      g.beginPath();
      for ( let x = x0 + mouth * 0.08; x <= x1 - mouth * 0.08; x += 1 ) { g.lineTo( x, lipAt( x ) ); }
    };
    g.save();
    g.lineCap = 'round';
    g.globalCompositeOperation = 'lighter';
    g.shadowColor = this.rgba( ember, 0.9 );
    g.shadowBlur = 10 * k * scale;
    lipPath();
    g.strokeStyle = this.rgba( ember, 0.85 );
    g.lineWidth = Math.max( 1.5, 2.2 * k );
    g.stroke();
    g.shadowBlur = 0;
    lipPath();
    // yellow in the middle, turning to the orange of the lava toward its ends (owner)
    const lg2 = g.createLinearGradient( x0, 0, x1, 0 );
    lg2.addColorStop( 0, this.rgba( orange, 0.9 ) );
    lg2.addColorStop( 0.22, this.rgba( orange, 0.9 ) );
    lg2.addColorStop( 0.78, this.rgba( orange, 0.9 ) );
    lg2.addColorStop( 0.5, this.rgba( this.mix( hotThread, [ 255, 250, 220 ], 0.3 ), 0.9 ) );
    lg2.addColorStop( 1, this.rgba( orange, 0.9 ) );
    g.strokeStyle = lg2;
    g.lineWidth = Math.max( 0.6, 0.8 * k );
    g.stroke();
    g.restore();
    this.back = c;

    // the front: the black ridge across the foot, where the watchers stand
    const fr = canvas();
    const f = fr.g;
    const fy = ( x: number ) => H * 0.86 + Math.sin( x / UW * 3.1 + 0.4 ) * H * 0.012 + Math.sin( x / UW * 9 + 1 ) * H * 0.004;
    f.beginPath();
    f.moveTo( 0, H );
    for ( let x = 0; x <= W; x += 3 ) { f.lineTo( x, fy( x ) ); }
    f.lineTo( W, H );
    f.closePath();
    f.fillStyle = this.rgba( this.mix( char, black, 0.75 ), 1 );
    f.fill();
    this.front = fr.c;
  }

  private seededLocal( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** The colour circle's five, or Etna's own: charcoal, red, lava orange, gold, ash. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 34, 33, 33 ], [ 215, 76, 87 ], [ 198, 100, 57 ], [ 226, 191, 121 ], [ 213, 207, 207 ] ];
  }
}
